import { z } from 'zod';
import { addDays } from './facilities-preventive.ts';
import { fromScaled, toScaled } from './money.ts';
import { convertAmount } from './quotations.ts';
import { decimalString } from './schemas.ts';

/**
 * Motor de projeção do caixa (ADR 0084, seção 3.4), versão 1: parte do saldo realizado de cada
 * conta no dia de referência e soma, dia a dia, os compromissos em aberto. Sem acesso ao banco de
 * dados: a mesma entrada dá sempre o mesmo resultado, na tela, na foto diária e nos testes.
 *
 * Entram os títulos a pagar aprovados (camada "compromisso", Entrega 3) e as ocorrências abertas
 * dos lançamentos previstos, pela sobra ainda não vinculada (camada "previsão", Entrega 4).
 * Orçamento não coberto e estimativas entram nas entregas seguintes, como novas camadas.
 */

export const CASHFLOW_ENGINE_VERSION = '2';
export const PROJECTION_HORIZONS = [7, 15, 30, 60, 90, 180, 365] as const;
export const MAX_PROJECTION_DAYS = 366;

export type ProjectionBasis =
  'scheduled' | 'due' | 'forecast' | 'expected' | 'estimate' | 'overdue';

export interface ProjectionAccountInput {
  /** Nulo: o balde "sem conta definida" da moeda. */
  accountKey: string | null;
  currency: string;
  /** Saldo realizado no fim do dia de referência; nulo se a conta não tem saldo de abertura. */
  opening: string | null;
}

export interface ProjectionItemInput {
  sourceType:
    | 'payables.installment'
    | 'treasury.forecast_occurrence'
    | 'treasury.forecast_payroll'
    | 'treasury.receipt'
    | 'treasury.budget_estimate'
    | 'treasury.scenario';
  sourceId: string;
  accountKey: string | null;
  currency: string;
  /** Data programada ou, sem programação, o vencimento. */
  date: string;
  /** Com sinal: entrada positiva, saída negativa. */
  amount: string;
  layer: 'commitment' | 'forecast' | 'receivable' | 'budget' | 'scenario';
  basis: 'scheduled' | 'due' | 'forecast' | 'expected' | 'estimate';
  label: string;
}

export interface ProjectionInput {
  asOf: string;
  horizonEnd: string;
  accounts: readonly ProjectionAccountInput[];
  items: readonly ProjectionItemInput[];
  /** Dia útil pelo calendário da empresa; nulo quando a empresa não tem calendário atribuído. */
  isBusinessDay: ((date: string) => boolean) | null;
  minimum: { currency: string; amount: string } | null;
}

export interface ProjectedItem extends Omit<ProjectionItemInput, 'basis'> {
  basis: ProjectionBasis;
  projectedDate: string;
}

export type ProjectionDayFlag = 'no_balance' | 'unassigned';

export interface ProjectionDayRow {
  accountKey: string | null;
  currency: string;
  date: string;
  opening: string | null;
  inflows: string;
  outflows: string;
  closing: string | null;
  flags: ProjectionDayFlag[];
}

export type ProjectionTotalFlag = 'negative' | 'below_minimum' | 'partial';

export interface ProjectionTotal {
  currency: string;
  date: string;
  /** Soma das contas com saldo e do balde sem conta; contas sem saldo ficam fora ("partial"). */
  closing: string;
  inflows: string;
  outflows: string;
  /** Vencidos e não pagos que entraram hoje, com sinal (saída negativa). */
  overdue: string;
  flags: ProjectionTotalFlag[];
}

export interface ProjectionSummary {
  currency: string;
  lowest: { date: string; closing: string };
  firstNegative: string | null;
  firstBelowMinimum: string | null;
  partial: boolean;
}

export interface ProjectionResult {
  items: ProjectedItem[];
  days: ProjectionDayRow[];
  totals: ProjectionTotal[];
  summaries: ProjectionSummary[];
}

/** Próximo dia útil (até 31 dias adiante); sem calendário, a data fica como está. */
export function nextBusinessDate(
  date: string,
  isBusinessDay: ((date: string) => boolean) | null,
): string {
  if (!isBusinessDay) return date;
  let d = date;
  for (let i = 0; i <= 31; i++) {
    if (isBusinessDay(d)) return d;
    d = addDays(d, 1);
  }
  return date;
}

/** Data em que o item entra na projeção (D-01: vencido e não pago entra hoje; D-09: dia útil). */
export function projectItem(
  item: ProjectionItemInput,
  asOf: string,
  isBusinessDay: ((date: string) => boolean) | null,
): ProjectedItem {
  if (item.date < asOf) return { ...item, basis: 'overdue', projectedDate: asOf };
  return { ...item, projectedDate: nextBusinessDate(item.date, isBusinessDay) };
}

const keyOf = (accountKey: string | null, currency: string) => `${accountKey ?? '-'}|${currency}`;

export function projectCash(input: ProjectionInput): ProjectionResult {
  const { asOf, horizonEnd } = input;
  const accounts = new Map(input.accounts.map((a) => [keyOf(a.accountKey, a.currency), a]));
  const items = input.items
    .map((it) => projectItem(it, asOf, input.isBusinessDay))
    .filter((it) => it.projectedDate <= horizonEnd)
    // Conta de outra moeda ou fora do que a pessoa vê: vai para o balde sem conta da moeda.
    .map((it) =>
      it.accountKey && !accounts.has(keyOf(it.accountKey, it.currency))
        ? { ...it, accountKey: null }
        : it,
    );
  for (const it of items) {
    const k = keyOf(null, it.currency);
    if (!it.accountKey && !accounts.has(k)) {
      accounts.set(k, { accountKey: null, currency: it.currency, opening: '0' });
    }
  }

  const flows = new Map<string, { inflows: bigint; outflows: bigint; overdue: bigint }>();
  for (const it of items) {
    const k = `${keyOf(it.accountKey, it.currency)}|${it.projectedDate}`;
    const cur = flows.get(k) ?? { inflows: 0n, outflows: 0n, overdue: 0n };
    const v = toScaled(it.amount);
    if (v >= 0n) cur.inflows += v;
    else cur.outflows -= v;
    if (it.basis === 'overdue') cur.overdue += v;
    flows.set(k, cur);
  }

  const days: ProjectionDayRow[] = [];
  const totals = new Map<
    string,
    {
      closing: bigint;
      inflows: bigint;
      outflows: bigint;
      overdue: bigint;
      partial: boolean;
    }
  >();
  for (const a of [...accounts.values()].sort((x, y) =>
    keyOf(x.accountKey, x.currency).localeCompare(keyOf(y.accountKey, y.currency)),
  )) {
    let balance = a.opening === null ? null : toScaled(a.opening);
    const flags: ProjectionDayFlag[] = [];
    if (a.opening === null) flags.push('no_balance');
    if (a.accountKey === null) flags.push('unassigned');
    for (let d = asOf; d <= horizonEnd; d = addDays(d, 1)) {
      const f = flows.get(`${keyOf(a.accountKey, a.currency)}|${d}`) ?? {
        inflows: 0n,
        outflows: 0n,
        overdue: 0n,
      };
      const opening = balance;
      balance = balance === null ? null : balance + f.inflows - f.outflows;
      days.push({
        accountKey: a.accountKey,
        currency: a.currency,
        date: d,
        opening: opening === null ? null : fromScaled(opening),
        inflows: fromScaled(f.inflows),
        outflows: fromScaled(f.outflows),
        closing: balance === null ? null : fromScaled(balance),
        flags,
      });
      const tk = `${a.currency}|${d}`;
      const t = totals.get(tk) ?? {
        closing: 0n,
        inflows: 0n,
        outflows: 0n,
        overdue: 0n,
        partial: false,
      };
      t.inflows += f.inflows;
      t.outflows += f.outflows;
      t.overdue += f.overdue;
      if (balance === null) t.partial = true;
      else t.closing += balance;
      totals.set(tk, t);
    }
  }

  const minimum = input.minimum;
  const totalRows: ProjectionTotal[] = [...totals.entries()]
    .map(([k, t]) => {
      const [currency, date] = k.split('|') as [string, string];
      const flags: ProjectionTotalFlag[] = [];
      if (t.closing < 0n) flags.push('negative');
      if (minimum && minimum.currency === currency && t.closing < toScaled(minimum.amount)) {
        flags.push('below_minimum');
      }
      if (t.partial) flags.push('partial');
      return {
        currency,
        date,
        closing: fromScaled(t.closing),
        inflows: fromScaled(t.inflows),
        outflows: fromScaled(t.outflows),
        overdue: fromScaled(t.overdue),
        flags,
      };
    })
    .sort((x, y) => x.currency.localeCompare(y.currency) || x.date.localeCompare(y.date));

  const summaries = [...new Set(totalRows.map((t) => t.currency))].map((currency) =>
    summarize(
      currency,
      totalRows.filter((t) => t.currency === currency),
    ),
  );
  return { items, days, totals: totalRows, summaries };
}

/** Quadro de uma moeda: menor saldo, primeiro dia negativo e abaixo do mínimo, se é parcial. */
function summarize(currency: string, rows: readonly ProjectionTotal[]): ProjectionSummary {
  const lowest = rows.reduce((a, b) => (toScaled(b.closing) < toScaled(a.closing) ? b : a));
  return {
    currency,
    lowest: { date: lowest.date, closing: lowest.closing },
    firstNegative: rows.find((t) => t.flags.includes('negative'))?.date ?? null,
    firstBelowMinimum: rows.find((t) => t.flags.includes('below_minimum'))?.date ?? null,
    partial: rows.some((t) => t.flags.includes('partial')),
  };
}

// ---------------------------------------------------------------------------
// Consolidação em uma moeda (D-08)
// ---------------------------------------------------------------------------

/** Taxa de uma moeda para a moeda da consolidação, como a regra única do câmbio devolve. */
export interface ConsolidationRate {
  currency: string;
  rate: string;
  /** A taxa gravada é a do par inverso: divide em vez de multiplicar. */
  inverse: boolean;
  rateDate: string;
  source: string | null;
  rateType: string | null;
}

export interface ConsolidatedProjection {
  currency: string;
  /** Uma por moeda convertida, para a tela mostrar a taxa, a data e a fonte. */
  rates: ConsolidationRate[];
  /** Moedas sem taxa: ficam fora do total, com aviso. */
  missing: string[];
  totals: ProjectionTotal[];
  summary: ProjectionSummary | null;
}

/**
 * Visão consolidada (ADR 0084, D-08): os totais de cada moeda convertidos para uma só, na
 * exibição, pela taxa de cada moeda (a mesma em todos os dias). Nada é gravado nem muda o quadro
 * de cada moeda. Moeda sem taxa fica fora do total e torna todos os dias parciais. O saldo mínimo
 * é convertido pela taxa da moeda dele; sem taxa, a consolidação não marca "abaixo do mínimo".
 */
export function consolidateProjection(
  totals: readonly ProjectionTotal[],
  target: string,
  rates: readonly ConsolidationRate[],
  minimum: { currency: string; amount: string } | null,
): ConsolidatedProjection {
  const rateOf = new Map(rates.map((r) => [r.currency, r]));
  const convert = (amount: string, currency: string): string | null => {
    if (currency === target) return amount;
    const r = rateOf.get(currency);
    if (!r) return null;
    // Converte o valor sem o sinal: o arredondamento fica igual para entrada e saída.
    const v = toScaled(amount);
    const abs = toScaled(convertAmount(fromScaled(v < 0n ? -v : v), r.rate, r.inverse));
    return fromScaled(v < 0n ? -abs : abs);
  };
  const used = new Set<string>();
  const missing = new Set<string>();
  const byDate = new Map<
    string,
    { closing: bigint; inflows: bigint; outflows: bigint; overdue: bigint; partial: boolean }
  >();
  for (const t of totals) {
    const day = byDate.get(t.date) ?? {
      closing: 0n,
      inflows: 0n,
      outflows: 0n,
      overdue: 0n,
      partial: false,
    };
    byDate.set(t.date, day);
    if (t.currency !== target && !rateOf.has(t.currency)) {
      missing.add(t.currency);
      continue;
    }
    if (t.currency !== target) used.add(t.currency);
    day.closing += toScaled(convert(t.closing, t.currency)!);
    day.inflows += toScaled(convert(t.inflows, t.currency)!);
    day.outflows += toScaled(convert(t.outflows, t.currency)!);
    day.overdue += toScaled(convert(t.overdue, t.currency)!);
    if (t.flags.includes('partial')) day.partial = true;
  }
  const min = minimum ? convert(minimum.amount, minimum.currency) : null;
  const rows: ProjectionTotal[] = [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, d]) => {
      const flags: ProjectionTotalFlag[] = [];
      if (d.closing < 0n) flags.push('negative');
      if (min !== null && d.closing < toScaled(min)) flags.push('below_minimum');
      if (d.partial || missing.size > 0) flags.push('partial');
      return {
        currency: target,
        date,
        closing: fromScaled(d.closing),
        inflows: fromScaled(d.inflows),
        outflows: fromScaled(d.outflows),
        overdue: fromScaled(d.overdue),
        flags,
      };
    });
  return {
    currency: target,
    rates: rates.filter((r) => used.has(r.currency)),
    missing: [...missing].sort(),
    totals: rows,
    summary: rows.length ? summarize(target, rows) : null,
  };
}

// ---------------------------------------------------------------------------
// Entradas da API
// ---------------------------------------------------------------------------

export const cashSettingsInput = z.object({
  minimumBalance: decimalString(15, 4),
  minimumCurrency: z.string().regex(/^[A-Z]{3}$/, 'Moeda com 3 letras'),
  consolidationCurrency: z
    .string()
    .regex(/^[A-Z]{3}$/, 'Moeda com 3 letras')
    .default('BRL'),
});

export const projectionSnapshotInput = z.object({
  legalEntityId: z.uuid(),
  days: z.number().int().min(1).max(MAX_PROJECTION_DAYS).default(90),
});
